import generateVersionHash from '@utils/generateVersionHash';
import { createStoreFetcher } from '@/lib/http';
import api from './constants';

const rekening = {
  namespaced: true,

  state() {
    return {
      loading: false,
    };
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },
  },

  actions: {
    /**
     * Checks an account number at the first step of the registration.
     *
     * The account is looked up first. A lookup without a status means the
     * account already belongs to a merchant; otherwise its validity is checked
     * next. Step 1 renders both outcomes inline, so they come back as values.
     *
     * A request that fails outright is left to reject. The shared layer has
     * already shown the reason in an alert, and step 1 ignores the rejection,
     * so a dropped connection is not mistaken for an invalid account.
     *
     * This module has no field errors, so the fetcher is told not to commit
     * them.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string} params.no_rekening - Account number entered by the user.
     * @returns {Promise<'registered'|boolean>} `'registered'` when the account
     *   is already taken, otherwise whether it is valid.
     * @throws {ApiError} When either request fails.
     */
    async validate(ctx, { date, no_rekening }) {
      const http = createStoreFetcher(ctx, { errorsMutation: null });

      const res = await http.post(api.ACCOUNT_NUMBER, {
        account_number: no_rekening,
        version: generateVersionHash(no_rekening, date),
      });

      const status = res.data?.status;
      if (status === null || status === undefined) return 'registered';

      const valid = await ctx.dispatch('validateRek', {
        date,
        noRekening: no_rekening,
      });

      return valid || false;
    },

    /**
     * Checks whether an unregistered account number is valid.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string} params.noRekening - Account number to check.
     * @returns {Promise<boolean>} True unless the account is reported invalid.
     * @throws {ApiError} When the request fails.
     */
    async validateRek(ctx, { date, noRekening }) {
      const http = createStoreFetcher(ctx, { errorsMutation: null });

      const res = await http.post(api.REKENING_VALIDATE, {
        nomerRekening: noRekening,
        version: generateVersionHash(noRekening, date),
      });

      return res.data?.status !== 'invalid';
    },
  },
};

export default rekening;
